import type { PaletteMode } from "@mui/material";
import { alpha } from "@mui/material";

interface RangeColors {
  band: string;
  edge: string;
  hover: string;
  contrastText: string;
}

interface SurfaceColors {
  sunken: string;
  raised: string;
}

interface BorderColors {
  subtle: string;
}

interface PaletteColors {
  primary: { main: string };
  secondary: { main: string };
  success: { main: string };
  warning: { main: string };
  error: { main: string };
  info: { main: string };
  background: { default: string; paper: string };
  text: { primary: string; secondary: string; disabled: string };
  divider: string;
  range: RangeColors;
  surface: SurfaceColors;
  border: BorderColors;
}

type Palettes = Record<PaletteMode, PaletteColors>;

const lightPrimary = "#007E88";
const lightPaper = "#FFFFFE";
const lightTextPrimary = "#1C1917";
const lightBackground = "#FAFAF7";

const darkPrimary = "#4DD0E1";
const darkBackground = "#1A1816";
const darkTextPrimary = "#F5F0EB";

export const palettes: Palettes = {
  light: {
    primary: { main: lightPrimary },
    secondary: { main: "#C84318" },
    success: { main: "#2E7D32" },
    warning: { main: "#F9A825" },
    error: { main: "#C62828" },
    info: { main: "#01579B" },
    background: { default: lightBackground, paper: lightPaper },
    text: { primary: lightTextPrimary, secondary: "#57534E", disabled: "#A8A29E" },
    divider: "#E7E5E4",
    range: {
      band: alpha(lightPrimary, 0.12),
      edge: lightPrimary,
      hover: alpha(lightPrimary, 0.08),
      contrastText: lightPaper,
    },
    surface: {
      sunken: "#F3F1ED",
      raised: lightPaper,
    },
    border: {
      subtle: alpha(lightTextPrimary, 0.08),
    },
  },
  dark: {
    primary: { main: darkPrimary },
    secondary: { main: "#FF8A65" },
    success: { main: "#66BB6A" },
    warning: { main: "#FFD54F" },
    error: { main: "#EF5350" },
    info: { main: "#29B6F6" },
    background: { default: darkBackground, paper: "#262220" },
    text: { primary: darkTextPrimary, secondary: "#A8A29E", disabled: "#78716C" },
    divider: alpha(darkTextPrimary, 0.12),
    range: {
      band: alpha(darkPrimary, 0.18),
      edge: darkPrimary,
      hover: alpha(darkPrimary, 0.1),
      contrastText: darkBackground,
    },
    surface: {
      sunken: "#161412",
      raised: "#2E2A27",
    },
    border: {
      subtle: alpha(darkTextPrimary, 0.1),
    },
  },
};
